/**
 * Confirm which staged bank line settles this fund transaction.
 *
 * The mirror of the Incoming Transactions inbox: there an operator starts from
 * a bank line and looks for the transaction it belongs to; here they start from
 * the transaction. Confirming a line that covers the full outstanding amount is
 * what flips bank confirmation to verified.
 */

import { useState, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useFundTransactionBankMatching,
  useMatchBankIncomingToFundTransaction,
} from '@/hooks/react-query/useFundTransactions';
import {
  BANK_TRANSACTION_TYPE_LABELS,
  MATCH_BASIS_LABELS,
  MATCH_BLOCKED_REASON_LABELS,
  type TransactionBankMatchView,
  type TransactionMatchCandidate,
} from '@/types/bank-reconciliation';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { sameToTheCent } from '@/utils/number-operations';

interface MatchPontoTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId: string;
  transactionId: string;
}

export function MatchPontoTransactionDialog({
  open,
  onOpenChange,
  fundId,
  transactionId,
}: MatchPontoTransactionDialogProps) {
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const { data, isLoading } = useFundTransactionBankMatching(
    fundId,
    transactionId,
    open
  );
  const match = useMatchBankIncomingToFundTransaction(fundId, transactionId);

  // A selection left over from a previous visit would confirm the wrong line,
  // so closing always clears it.
  const close = (next: boolean) => {
    if (!next) setChoiceId(null);
    onOpenChange(next);
  };

  const candidates = data?.candidates ?? [];
  const matchable = candidates.filter((candidate) => candidate.can_match);
  const remaining = data?.remaining_amount ?? '0';
  const currency = data?.currency ?? undefined;

  const handleConfirm = () => {
    if (!choiceId) return;
    match.mutate(choiceId, { onSuccess: () => close(false) });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Match a bank transaction</DialogTitle>
          <DialogDescription>
            Pick the bank line that settles this transaction.{' '}
            <span className="font-medium text-foreground">
              {formatCurrency(remaining, { currency })}
            </span>{' '}
            is still outstanding - confirming a line for the full amount records
            bank confirmation and marks the transaction verified.
          </DialogDescription>
        </DialogHeader>

        {isLoading ||
        !data?.is_reconcilable ||
        !data.is_open ||
        candidates.length === 0 ? (
          <CandidatesPlaceholder isLoading={isLoading} data={data} />
        ) : (
          <div className="space-y-3">
            {matchable.length === 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>
                  No line can be confirmed as it stands - see the reason on each
                  row.
                </span>
              </div>
            )}

            <div className="border rounded-lg">
              <Table data-testid="ponto-candidates">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]" />
                    <TableHead className="w-[110px]">DATE</TableHead>
                    <TableHead className="w-[180px]">COUNTERPARTY</TableHead>
                    <TableHead>DESCRIPTION</TableHead>
                    <TableHead className="w-[150px]">ACCOUNT</TableHead>
                    <TableHead className="text-right w-[140px]">
                      AMOUNT
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.map((candidate) => (
                    <CandidateRow
                      key={candidate.id}
                      candidate={candidate}
                      selected={choiceId === candidate.id}
                      onSelect={() => setChoiceId(candidate.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {data.truncated && (
              <p className="text-xs text-muted-foreground">
                Showing the closest matches only. Narrow the search from
                Incoming Transactions if the line you need is not here.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!choiceId || match.isPending}
            data-testid="confirm-ponto-match"
          >
            {match.isPending ? 'Matching…' : 'Confirm match'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidatesPlaceholder({
  isLoading,
  data,
}: {
  isLoading: boolean;
  data: TransactionBankMatchView | undefined;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Loading bank transactions…
      </div>
    );
  }
  if (!data?.is_reconcilable) {
    return (
      <EmptyState>
        This transaction type carries no cash leg, so no bank line can settle
        it.
      </EmptyState>
    );
  }
  if (!data.is_open) {
    return (
      <EmptyState>
        This transaction is no longer open, so it accepts no further bank lines.
      </EmptyState>
    );
  }
  return (
    <EmptyState>
      {data.unmatched_line_count === 0
        ? 'No bank transactions are awaiting a match in this fund. Sync the bank feed and try again.'
        : `None of the ${data.unmatched_line_count} unmatched bank transactions in this fund move the right way, in the right currency, to settle this one.`}
    </EmptyState>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="py-10 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function CandidateRow({
  candidate,
  selected,
  onSelect,
}: {
  candidate: TransactionMatchCandidate;
  selected: boolean;
  onSelect: () => void;
}) {
  const description =
    candidate.remittance_information ||
    candidate.end_to_end_id ||
    BANK_TRANSACTION_TYPE_LABELS[candidate.bank_transaction_type];

  return (
    <TableRow
      className={candidate.can_match ? 'cursor-pointer' : 'opacity-60'}
      onClick={candidate.can_match ? onSelect : undefined}
    >
      <TableCell>
        <input
          type="radio"
          name="ponto-candidate"
          checked={selected}
          disabled={!candidate.can_match}
          onChange={onSelect}
          aria-label={`Select bank line of ${candidate.amount}`}
        />
      </TableCell>
      <TableCell className="align-top">
        {formatDate(candidate.value_date ?? candidate.execution_date)}
      </TableCell>
      <TableCell className="align-top">
        {candidate.counterparty_name || candidate.counterparty_reference || '-'}
      </TableCell>
      <TableCell className="align-top max-w-[320px]">
        <div className="space-y-1">
          <p className="truncate">{description}</p>
          {candidate.suggested && (
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="gap-1 whitespace-nowrap">
                <Sparkles className="h-3 w-3" />
                {candidate.suggestion_basis
                  ? MATCH_BASIS_LABELS[candidate.suggestion_basis]
                  : 'Suggested'}
              </Badge>
            </div>
          )}
          {candidate.suggestion_note && (
            <p className="text-xs text-muted-foreground">
              {candidate.suggestion_note}
            </p>
          )}
          {candidate.blocked_reason && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {MATCH_BLOCKED_REASON_LABELS[candidate.blocked_reason]}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="text-sm">{candidate.account_name}</div>
        <div className="text-xs text-muted-foreground">
          {candidate.bank_name}
        </div>
      </TableCell>
      <TableCell className="align-top text-right font-medium">
        {formatCurrency(candidate.unapplied_amount, {
          currency: candidate.currency ?? undefined,
        })}
        {!sameToTheCent(
          Number(candidate.unapplied_amount),
          Number(candidate.amount)
        ) && (
          <div className="text-xs font-normal text-muted-foreground">
            of{' '}
            {formatCurrency(candidate.amount, {
              currency: candidate.currency ?? undefined,
            })}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
